/**
 * Module dependencies.
 */

import { MediaProps, ProductProps } from 'src/types/products';
import { PlayVideoButton } from 'src/components/core/video-player';
import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { color, media, units } from 'src/styles/utils';
import { downloadPdfFile } from 'src/core/utils/downloads';
import { fetchProductPdf } from 'src/api/app/products/fetch-product-pdf';
import { finishesValues } from 'src/core/collections-config/finishes';
import { functions } from 'src/core/collections-config/functions';
import { getKeyFromObject } from 'src/core/utils/objects';
import { ifProp, theme } from 'styled-tools';
import { kioskRoutes } from 'src/core/routes';
import { mediasProductName } from 'src/core/collections-config/media';
import { routeTemplate, useLocalizedRoute } from 'src/core/utils/routes';
import { setFontStyle } from 'src/styles/utils/typography';
import { typography } from 'src/styles/type';
import { typologiesValues } from 'src/core/collections-config/typologies';
import { useComparator } from 'src/context/comparator/context';
import { useTranslate } from 'src/core/utils/translator';
import Box from 'src/components/core/layout/box';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import FavoritesMiddleman, {
  FavoriteState,
  favoriteStates
} from 'src/components/my-revigres/favorites/favorites-middleman';

import GalleryNavigation from 'src/components/core/slider/gallery-navigation';
import Image from 'src/components/core/image';
import ImageDownloadButton from 'src/components/core/buttons/image-download-button';
import Lightbox from 'src/components/lightbox';
import Navigation from 'src/components/navigation';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import ShareButton from 'src/components/core/buttons/share-button';
import Slider, { Slide } from 'src/components/core/slider';
import Type from 'src/components/core/typography';
import compact from 'lodash/compact';
import compareAddIcon from 'src/assets/svg/compare-add.svg';
import compareIcon from 'src/assets/svg/compare.svg';
import concat from 'lodash/concat';
import downloadIcon from 'src/assets/svg/download.svg';
import favoriteAddIcon from 'src/assets/svg/favorite-add.svg';
import favoriteIcon from 'src/assets/svg/favorite.svg';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = Pick<
  ProductProps,
  | 'descriptionSeo'
  | 'finish'
  | 'format'
  | 'function'
  | 'id'
  | 'media'
  | 'name'
  | 'productCollectionSlug'
  | 'slug'
  | 'typology'
  | 'videoUrl'
> & {
  isKiosk?: boolean;
};

/**
 * `Section` styled component.
 */

const Section = styled.header`
  background-color: ${color('white')};
  padding: calc(${units(6)} + ${theme('dimensions.navbarHeightMobile')}px) 0 ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${units(10)} + ${theme('dimensions.navbarHeight')}px) 0 ${units(25)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'navigation'
    'details'
    'gallery'
    'actions';
  grid-template-columns: 100%;

  @media (min-width: 375px) {
    grid-template-areas:
      'navigation navigation navigation'
      'details    details    details'
      '.          gallery    .'
      '.          actions    .';
    grid-template-columns: 1fr 10fr 1fr;
  }

  ${media.min('ms')`
    grid-template-areas:
      'navigation navigation .'
      'details    details .'
      '.          gallery .'
      '.          actions .';
    grid-template-columns: 1fr 10fr 1fr;
  `}

  ${media.min('md')`
    grid-template-areas:
      '. navigation navigation .       .'
      '. details    details    details .'
      '. actions    gallery    gallery .';
    grid-template-columns: 1fr 3fr 6fr repeat(2, 1fr);
  `}

  ${media.min('xl')`
    grid-template-areas:
      '. navigation navigation .'
      '. details    details    .'
      '. actions    gallery    .';
    grid-template-columns: 1fr 3fr 6fr 2fr;
  `}
`;

/**
 * `StyledNavigation` styled component.
 */

const StyledNavigation = styled(Navigation)`
  grid-area: navigation;
  margin-bottom: ${units(5.5)};

  ${media.min('ms')`
    padding-left: ${units(3.5)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled.h1`
  ${setFontStyle({
    fontFamily: typography.fontFamily.serif,
    fontSize: 112,
    fontSizeMax: 112,
    fontSizeMin: 44,
    fontWeight: 400,
    letterSpacing: 0,
    lineHeight: 112
  })}

  span {
    display: block;
  }
`;

/**
 * `DetailsWrapper` styled component.
 */

const DetailsWrapper = styled.div`
  grid-area: details;
  margin-bottom: ${units(2.5)};

  ${media.min('md')`
    margin-bottom: ${units(2)};
  `}
`;

/**
 * `Format` styled component.
 */

const Format = styled(Type.Small).attrs({
  as: 'h2',
  small: true
})`
  color: ${color('brown400')};
`;

/**
 * `Finish` styled component.
 */

const Finish = styled(Type.Small).attrs({
  as: 'p',
  small: true
})`
  color: ${color('brown400')};
  text-transform: uppercase;
`;

/**
 * `Function` styled component.
 */

const Function = styled(Type.Small).attrs({
  as: 'p',
  small: true
})`
  color: ${color('grey300')};
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  grid-area: actions;
  padding-top: ${units(3)};
  z-index: 2;
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)<{ isLoading?: boolean }>`
  background-color: transparent;
  border: 1px solid transparent;
  color: ${color('grey400')};

  svg {
    color: ${color('black')};
    transition: color ${theme('animations.defaultTransition')};

    g path {
      transition: fill ${theme('animations.defaultTransition')};
    }
  }

  &:focus,
  &:hover {
    svg {
      color: ${color('white')};

      g path {
        fill: ${color('white')};

        &:first-child {
          fill: ${color('grey300')};
        }
      }
    }
  }

  ${ifProp('isLoading', css`
    background-color: ${color('grey900')};
    border-color: ${color('grey900')};
    color: ${color('white')};
  `)}
`;

/**
 * `GalleryWrapper` styled component.
 */

const GalleryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: gallery;
  overflow: hidden;
  z-index: 1;
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled(Slider)`
  max-height: 700px;
  overflow: hidden;
  width: 100%;
`;

/**
 * `GallerySlider` styled component.
 */

const GallerySlider = styled.div`
  position: relative;
`;

/**
 * `DownloadImageButton` styled component.
 */

const DownloadImageButton = styled(ImageDownloadButton)`
  bottom: ${units(2)};
  position: absolute;
  right: ${units(2)};
  z-index: 2;

  ${media.min('ms')`
    bottom: ${units(3)};
    right: ${units(3)};
  `}
`;

/**
 * `SlideItem` styled component.
 */

const SlideItem = styled.div`
  background-color: ${color('grey100')};
  cursor: pointer;
  overflow: hidden;
  padding-bottom: 100%;
`;

/**
 * `PaginationWrapper` styled component.
 */

const PaginationWrapper = styled.div`
  display: none;

  ${media.min('ms')`
    display: flex;
    justify-content: flex-end;
    padding-top: ${units(4)};
  `}
`;

/**
 * `Classification` styled component.
 */

const Classification = styled(Type.H6).attrs({
  as: 'span'
})`
  padding-top: ${units(1)};
`;

/**
 * Get images.
 */

function getImages(medias: MediaProps[]): MediaProps[] | null {
  return filter(medias, ({ collectionName }) => {
    const isFeaturedImage = collectionName === mediasProductName.featuredImages;
    const isGallery = collectionName === mediasProductName.gallery;

    return isFeaturedImage || isGallery;
  });
}

/**
 * Get video image.
 */

function getVideoImage(medias: MediaProps[]): string | null {
  const videoMedia = filter(medias, { collectionName: mediasProductName.videoImage });

  return get(head(videoMedia), 'fileUrl');
}

/**
 * `Header` component.
 */

const Header = (props: Props): ReactElement => {
  const {
    descriptionSeo,
    finish,
    format,
    id,
    isKiosk,
    media,
    name,
    productCollectionSlug,
    slug,
    typology,
    videoUrl
  } = props;

  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const tipologyKey = getKeyFromObject(typologiesValues, typology);
  const finishKey = getKeyFromObject(finishesValues, finish);
  const functionKey = getKeyFromObject(functions, props.function);
  const formatApproximatedValue = useMemo(() => {
    return format?.approximated ? `[ ${format.approximated.split('.').join(',')} cm ]` : '';
  }, [format]);

  const headerGallery = useMemo(() => {
    const images = getImages(media);
    const videoImage = getVideoImage(media);

    return compact(concat(videoImage && videoUrl ? {
      coverImage: videoImage,
      type: 'video',
      url: videoUrl
    } : null, map(images, ({ customProperties, description, fileUrl }) => ({
      alt: customProperties?.title,
      label: description,
      type: 'image',
      url: fileUrl
    }))));
  }, [media, videoUrl]);

  const gallerySize = size(headerGallery);
  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isOpenLightbox, setOpenLightbox] = useState<boolean>();
  const handleSlideChange = useCallback(({ activeIndex }) => {
    setActiveSlide(activeIndex);
  }, []);

  const handleClickSlide = useCallback(() => {
    setOpenLightbox(true);
  }, []);

  const activeImageUrl = useMemo(() => {
    const activeItem = headerGallery[activeSlide];

    return activeItem?.type === 'image' ? activeItem.url : null;
  }, [activeSlide, headerGallery]);

  const handleClickDot = useCallback((index: number) => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideTo(index);
    }
  }, []);

  const emailSubject = useMemo(() => {
    const finish = translate(`collections:finishes.${finishKey}`);

    return `${name} ${format} ${formatApproximatedValue} ${finish}`;
  }, [finishKey, format, formatApproximatedValue, name, translate]);

  const [isLoadingPdf, setLoadingPdf] = useState<boolean>();
  const handleGetPdf = useCallback(() => {
    setLoadingPdf(true);

    fetchProductPdf({ locale, slug }).then(({ data }) => {
      setLoadingPdf(false);

      return downloadPdfFile(data, slug);
    });
  }, [locale, slug]);

  const [isFavorite, setIsFavorite] = useState<FavoriteState>(null);
  const [isFavoriteTriggered, setIsFavoriteTriggered] = useState<boolean>(false);
  const [isCompare, setIsCompare] = useState<boolean>();
  const isMobile = useBreakpoint('md', 'max');
  const { onAdd, onRemove, products } = useComparator();
  const isInComparator = useMemo<boolean>(() => {
    return !isEmpty(filter(products, product => product.id === id));
  }, [id, products]);

  const isDisabled = useMemo<boolean>(() => {
    return size(products) === (isMobile ? 2 : 3) && !isInComparator;
  }, [isInComparator, isMobile, products]);

  const kioskCollectionRoute = routeTemplate(kioskRoutes.collection, { slug: productCollectionSlug });
  const collectionRoute = routeResolver('collection', { slug: productCollectionSlug });
  const compareButtonRef = useRef<HTMLButtonElement>();
  const handleToggleCompare = useCallback(() => {
    if (compareButtonRef.current) {
      compareButtonRef.current.blur();
    }

    setIsCompare(!isCompare);

    if (isCompare) {
      onRemove(id);

      return;
    }

    onAdd({
      finish: finishKey,
      format,
      formatApproximatedValue,
      id,
      imageUrl: head(headerGallery)?.url ?? assetUrlResolve('/static/images/empty/list@2x.jpg'),
      name,
      slug
    });
  }, [
    finishKey,
    format,
    formatApproximatedValue,
    headerGallery,
    id,
    isCompare,
    name,
    onAdd,
    onRemove,
    slug
  ]);

  useEffect(() => {
    setIsCompare(isInComparator);
  }, [isInComparator]);

  useEffect(() => {
    if (sliderRef && sliderRef.current && isOpenLightbox) {
      sliderRef.current.swiper.autoplay.stop();

      return;
    }

    if (sliderRef && sliderRef.current && !isOpenLightbox) {
      sliderRef.current.swiper.autoplay.start();

      return;
    }
  }, [isOpenLightbox]);

  return (
    <>
      <Section>
        <Container hasPadding={!isKiosk}>
          <Grid>
            <StyledNavigation
              href={isKiosk ? kioskCollectionRoute : collectionRoute}
              label={translate('products:actions.viewCollection')}
            />

            <DetailsWrapper>
              <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                <Title>
                  {`${name} `}

                  {tipologyKey && (
                    <Classification>
                      {translate(
                        `collections:classifications.${tipologyKey}`
                      )}
                    </Classification>
                  )}
                </Title>
              </ParallaxFadeInUp>

              <ParallaxFadeInUp
                parallaxOptions={{
                  percentage: 0.1,
                  speed: 2
                }}
              >
                {format && (
                  <Format>
                    {`${format.name} ${formatApproximatedValue}`}
                  </Format>
                )}

                {finishKey && (
                  <Finish>
                    {translate(`collections:finishes.${finishKey}`)}
                  </Finish>
                )}

                {functionKey && (
                  <Function>
                    {translate(`products:functions.${functionKey}`)}
                  </Function>
                )}
              </ParallaxFadeInUp>
            </DetailsWrapper>

            <Actions>
              <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                {!isKiosk && (
                  <>
                    <Box marginBottom={units(2)}>
                      <Button
                        href={routeResolver('whereToBuy')}
                        locale={locale}
                      >
                        {translate('common:actions.whereToBuy')}
                      </Button>
                    </Box>

                    <Box marginBottom={units(5)}>
                      <Button
                        href={routeResolver('productContact', { slug })}
                        locale={locale}
                        variant={'outlined'}
                      >
                        {translate('common:actions.contactUs')}
                      </Button>
                    </Box>

                    <Box marginBottom={units(1)}>
                      <StyledButton
                        disabled={!isFavorite || isFavoriteTriggered}
                        icon={isFavorite === favoriteStates.on ? favoriteAddIcon : favoriteIcon}
                        isLoading={!isFavorite || isFavoriteTriggered}
                        onClick={() => setIsFavoriteTriggered(true)}
                      >
                        {translate('common:actions.save')}
                      </StyledButton>
                    </Box>
                  </>
                )}

                <Box marginBottom={units(1)}>
                  <StyledButton
                    disabled={isDisabled}
                    icon={isCompare ? compareAddIcon : compareIcon}
                    onClick={handleToggleCompare}
                    ref={compareButtonRef}
                  >
                    {translate('common:actions.compare')}
                  </StyledButton>
                </Box>

                {!isKiosk && (
                  <>
                    <Box marginBottom={units(1)}>
                      <ShareButton
                        description={descriptionSeo}
                        emailSubject={emailSubject}
                        imageUrl={headerGallery[0]?.url}
                        title={name}
                      />
                    </Box>

                    <div>
                      <StyledButton
                        icon={downloadIcon}
                        isLoading={isLoadingPdf}
                        onClick={handleGetPdf}
                      >
                        {translate('products:actions.download')}
                      </StyledButton>
                    </div>
                  </>
                )}
              </ParallaxFadeInUp>
            </Actions>

            <GalleryWrapper>
              <FadeInUpAnimation>
                <GallerySlider>
                  <Gallery
                    onClick={handleClickSlide}
                    onSlideChange={handleSlideChange}
                    ref={sliderRef}
                  >
                    {map(headerGallery, ({ alt, coverImage, type, url }, index: number) => (
                      <Slide key={index}>
                        <SlideItem>
                          {type === 'image' && (
                            <Image
                              alt={alt || name}
                              layout={'fill'}
                              objectFit={'scale-down'}
                              objectPosition={'center'}
                              src={url}
                            />
                          )}

                          {type === 'video' && (
                            <>
                              <Image
                                alt={alt || name}
                                layout={'fill'}
                                objectFit={'cover'}
                                objectPosition={'center'}
                                src={coverImage}
                              />

                              <PlayVideoButton />
                            </>
                          )}
                        </SlideItem>
                      </Slide>
                    ))}
                  </Gallery>

                  {!isKiosk && activeImageUrl && (
                    <DownloadImageButton url={activeImageUrl} />
                  )}
                </GallerySlider>

                {gallerySize > 1 && (
                  <PaginationWrapper>
                    <GalleryNavigation
                      current={activeSlide}
                      displayPagination
                      onGoToNext={() => handleClickDot(activeSlide + 1)}
                      onGoToPrevious={() => handleClickDot(activeSlide - 1)}
                      total={gallerySize}
                    />
                  </PaginationWrapper>
                )}
              </FadeInUpAnimation>
            </GalleryWrapper>
          </Grid>
        </Container>
      </Section>

      {gallerySize > 0 && (
        <Lightbox
          initialSlide={activeSlide}
          isOpen={isOpenLightbox}
          items={headerGallery}
          onClose={() => setOpenLightbox(false)}
        />
      )}

      {!isKiosk && (
        <FavoritesMiddleman
          isFavorite={isFavorite}
          isFavoriteTriggered={isFavoriteTriggered}
          onChangeFavoriteState={setIsFavorite}
          onChangeFavoriteTriggered={setIsFavoriteTriggered}
          productId={id}
          productName={name}
        />
      )}
    </>
  );
};

/**
 * Export `Header` component.
 */

export default Header;
